/**
 * Utility functions for handling job preferences data
 * Converts between backend object format and frontend string array format
 */

/**
 * Job Category object from backend
 */
export interface JobCategoryObject {
  id: number;
  categoryKey: string;
}

/**
 * Employment Type object from backend
 */
export interface EmploymentTypeObject {
  id: number;
  employmentTypeKey: string;
  sortOrder?: number;
  active?: boolean;
}

/**
 * Job Position object from backend
 */
export interface JobPositionObject {
  id: number;
  positionKey: string;
  uuid: string;
  jobCategoryId: number;
}

/**
 * Extract category keys from backend response
 * Handles both old string array format and new object format for backward compatibility
 */
export function extractJobCategoryKeys(jobCategories: any): string[] {
  if (!jobCategories) return [];

  // Handle string format (old API or JSON string)
  if (typeof jobCategories === 'string') {
    try {
      const parsed = JSON.parse(jobCategories);
      if (Array.isArray(parsed)) {
        // If it's already an array of strings
        if (typeof parsed[0] === 'string') {
          return parsed;
        }
        // If it's an array of objects
        return parsed.map((item: any) => item.categoryKey || item);
      }
    } catch {
      return [];
    }
  }

  // Handle array format (new API)
  if (Array.isArray(jobCategories)) {
    return jobCategories.map((item: any) => {
      if (typeof item === 'string') {
        return item;
      }
      return item.categoryKey || '';
    }).filter(key => key !== '');
  }

  return [];
}

/**
 * Extract employment type keys from backend response
 * Handles both old string array format and new object format for backward compatibility
 */
export function extractEmploymentTypeKeys(employmentTypes: any): string[] {
  if (!employmentTypes) return [];

  // Handle string format (old API or JSON string)
  if (typeof employmentTypes === 'string') {
    try {
      const parsed = JSON.parse(employmentTypes);
      if (Array.isArray(parsed)) {
        // If it's already an array of strings
        if (typeof parsed[0] === 'string') {
          return parsed;
        }
        // If it's an array of objects
        return parsed.map((item: any) => item.employmentTypeKey || item);
      }
    } catch {
      return [];
    }
  }

  // Handle array format (new API)
  if (Array.isArray(employmentTypes)) {
    return employmentTypes.map((item: any) => {
      if (typeof item === 'string') {
        return item;
      }
      return item.employmentTypeKey || '';
    }).filter(key => key !== '');
  }

  return [];
}

/**
 * Extract position keys from backend response
 * Handles both old string array format and new object format for backward compatibility
 */
export function extractJobPositionKeys(jobPositions: any): string[] {
  if (!jobPositions) return [];

  // Handle string format (old API or JSON string)
  if (typeof jobPositions === 'string') {
    try {
      const parsed = JSON.parse(jobPositions);
      if (Array.isArray(parsed)) {
        // If it's already an array of strings
        if (typeof parsed[0] === 'string') {
          return parsed;
        }
        // If it's an array of objects
        return parsed.map((item: any) => item.positionKey || item);
      }
    } catch {
      return [];
    }
  }

  // Handle array format (new API)
  if (Array.isArray(jobPositions)) {
    return jobPositions.map((item: any) => {
      if (typeof item === 'string') {
        return item;
      }
      return item.positionKey || '';
    }).filter(key => key !== '');
  }

  return [];
}
